import { ChangeEvent, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link } from "react-router-dom";
import auth from "../../fire";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { ClipLoader } from "react-spinners";
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PageHeader from "../../components/PageHeader/PageHeader";

interface ChangePasswordValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword = () => {
  const [user] = useAuthState(auth);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const changePasswordSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Current password required"),
    newPassword: Yup.string()
      .min(7, "Password must be at least 7 characters")
      .max(50, "Password too long")
      .matches(/(?=.*[0-9])/, "Password must contain at least 1 number")
      .required("New password required")
      .notOneOf(
        [Yup.ref("currentPassword")],
        "Must be different from your current password"
      ),
    confirmPassword: Yup.string()
      .required("Please confirm your new password")
      .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
  });

  const toggleButton = (
    <button
      type="button"
      className="absolute inset-y-0 right-0 z-10 flex items-center pr-3 mt-1"
      onClick={() => setShowPassword((prev) => !prev)}
      tabIndex={-1}
    >
      {showPassword ? (
        <EyeSlashIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      ) : (
        <EyeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      )}
    </button>
  );

  if (success) {
    return (
      <>
        <PageHeader backLink="/account" pageTitle="Change Password" />
        <div className="flex flex-col py-12 px-4">
          <CheckCircleIcon
            className="h-auto w-12 mx-auto text-indigo-600"
            aria-hidden="true"
          />
          <h2 className="text-center mt-6 text-2xl font-bold tracking-tight text-gray-900">
            Password updated
          </h2>
          <p className="text-center text-sm text-gray-900 mt-2">
            Your password has been changed. You can keep using the app — there's
            no need to log in again.
          </p>
          <div className="px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
            <Link to="/account" className="text-sm text-indigo-600 font-medium">
              Back to account
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader backLink="/account" pageTitle="Change Password" />
      <div className="p-4">
        <h2 className="mb-1 font-bold text-gray-900 text-xl tracking-tight">
          Change Account Password
        </h2>
        <p className="mb-4 text-sm text-gray-600">
          Enter your current password and choose a new one. You'll stay logged in
          on this device after the change.
        </p>
        <Formik<ChangePasswordValues>
          initialValues={{
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={changePasswordSchema}
          onSubmit={async (values, { setStatus }) => {
            setStatus("");

            if (!user || !user.email) {
              setStatus({
                error: "You must be logged in to change your password.",
              });
              return;
            }

            // Re-authenticate first: this proves it's really the account owner
            // and satisfies Firebase's recent-login requirement for password
            // changes.
            try {
              const cred = EmailAuthProvider.credential(
                user.email,
                values.currentPassword
              );
              await reauthenticateWithCredential(user, cred);
            } catch (e) {
              setStatus({
                error: "Incorrect current password. Please try again.",
              });
              return;
            }

            try {
              await updatePassword(user, values.newPassword);
              setSuccess(true);
            } catch (error) {
              const err = error as { code?: string };
              setStatus({
                error:
                  err.code === "auth/weak-password"
                    ? "That password is too weak. Please choose a stronger one."
                    : "Could not update your password. Please try again.",
              });
            }
          }}
        >
          {({
            isSubmitting,
            status,
            setFieldValue,
            setStatus,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="currentPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Current password
                  </label>
                  <div className="relative">
                    <Field
                      id="currentPassword"
                      name="currentPassword"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pr-10"
                      placeholder="Current password"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("currentPassword", e.target.value);
                        setStatus({ error: "" });
                      }}
                    />
                    {toggleButton}
                  </div>
                  <div className="mt-1 min-h-[1.25rem]">
                    <ErrorMessage name="currentPassword">
                      {(msg) => (
                        <div className="text-sm text-red-600">*{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    New password
                  </label>
                  <div className="relative">
                    <Field
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pr-10"
                      placeholder="New password"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("newPassword", e.target.value);
                        setStatus({ error: "" });
                      }}
                    />
                    {toggleButton}
                  </div>
                  <div className="mt-1 min-h-[1.25rem]">
                    <ErrorMessage name="newPassword">
                      {(msg) => (
                        <div className="text-sm text-red-600">*{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Confirm new password
                  </label>
                  <div className="relative">
                    <Field
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pr-10"
                      placeholder="Confirm new password"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("confirmPassword", e.target.value);
                        setStatus({ error: "" });
                      }}
                    />
                    {toggleButton}
                  </div>
                  <div className="mt-1 min-h-[1.25rem]">
                    <ErrorMessage name="confirmPassword">
                      {(msg) => (
                        <div className="text-sm text-red-600">*{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="mt-4 group relative flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:pointer-events-none"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <ClipLoader
                    color="white"
                    speedMultiplier={0.6}
                    size={"1.25rem"}
                  />
                ) : (
                  "Update password"
                )}
              </button>

              {status && status.error ? (
                <div className="mt-2 text-sm text-red-600 whitespace-pre-wrap">
                  *{status.error}
                </div>
              ) : null}
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ChangePassword;
